import React, { useEffect, useRef } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "./AuthContext";
import { trackPageView } from "./activityTracker";
import Login from "./pages/Login";
import Hub from "./pages/Hub";
import Candidates from "./pages/Candidates";
import Clients from "./pages/Clients";
import Calendar from "./pages/Calendar";
import Analytics from "./pages/Analytics";
import Staff from "./pages/Staff";
import Kedatangan from "./pages/Kedatangan";
import Reports from "./pages/Reports";
import Book from "./pages/Book";
import Chat from "./pages/Chat";
import Bills from "./pages/Bills";
import Tasks from "./pages/Tasks";
import Invoice from "./pages/Invoice";
import Vehicles from "./pages/Vehicles";
import WorkersRegister from "./pages/WorkersRegister";
import JnJ from "./pages/JnJ";
import FitLah from "./pages/FitLah";
import Permits from "./pages/Permits";
import Payouts from "./pages/Payouts";
import Accounting from "./pages/Accounting";
import Stock from "./pages/Stock";
import ClockIn from "./pages/ClockIn";
import ComingSoon from "./components/ComingSoon";

function Protected() {
  const { user, checking } = useAuth();
  const location = useLocation();
  // Track every authenticated route change (page view) — fires after the
  // route renders, debounced naturally by React Router's single-fire-per-nav.
  // Public /login and /clock-in are excluded because they live outside
  // <Protected>. Same path repeated (e.g. tab focus re-render) doesn't
  // re-fire because `useEffect` deps cover both pathname+search.
  const lastPathRef = useRef("");
  useEffect(() => {
    if (!user) return;
    const path = location.pathname + (location.search || "");
    if (path === lastPathRef.current) return;
    lastPathRef.current = path;
    try { trackPageView(path); } catch { /* never break navigation */ }
  }, [user, location.pathname, location.search]);

  if (checking) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function ModuleGuard({ module, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = user.role === "admin";
  const allowed = isAdmin || (user.modules_allowed || []).includes(module);
  if (!allowed) return <Navigate to="/hub" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user || user.role !== "admin") return <Navigate to="/hub" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          theme="dark"
          toastOptions={{
            style: { background: "#111827", border: "1px solid rgba(148,163,184,0.15)", color: "#f1f5f9" },
          }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* PUBLIC worker self clock-in (no auth — accessed via rotating QR) */}
          <Route path="/clock-in" element={<ClockIn />} />
          <Route element={<Protected />}>
            <Route path="/" element={<Navigate to="/hub" replace />} />
            <Route path="/hub" element={<Hub />} />
            <Route path="/candidates" element={<ModuleGuard module="candidates"><Candidates /></ModuleGuard>} />
            <Route path="/leads" element={<Navigate to="/calendar" replace />} />
            <Route path="/calendar" element={<ModuleGuard module="calendar"><Calendar /></ModuleGuard>} />
            <Route path="/clients" element={<ModuleGuard module="clients"><Clients /></ModuleGuard>} />
            <Route path="/kedatangan" element={<ModuleGuard module="kedatangan">
              <Kedatangan />
            </ModuleGuard>} />
            <Route path="/reports" element={<ModuleGuard module="kedatangan">
              <Reports />
            </ModuleGuard>} />
            <Route path="/book" element={<ModuleGuard module="book">
              <Book />
            </ModuleGuard>} />
            <Route path="/analytics" element={<ModuleGuard module="analytics"><Analytics /></ModuleGuard>} />
            <Route path="/chat" element={<ModuleGuard module="chat"><Chat /></ModuleGuard>} />
            <Route path="/bills" element={<ModuleGuard module="bills"><Bills /></ModuleGuard>} />
            <Route path="/tasks" element={<ModuleGuard module="tasks"><Tasks /></ModuleGuard>} />
            <Route path="/invoice" element={<ModuleGuard module="invoice"><Invoice /></ModuleGuard>} />
            <Route path="/vehicles" element={<ModuleGuard module="vehicles"><Vehicles /></ModuleGuard>} />
            <Route path="/workers" element={<ModuleGuard module="workers"><WorkersRegister /></ModuleGuard>} />
            <Route path="/jnj" element={<ModuleGuard module="jnj"><JnJ /></ModuleGuard>} />
            <Route path="/fitlah" element={<ModuleGuard module="fitlah"><FitLah /></ModuleGuard>} />
            <Route path="/permits" element={<ModuleGuard module="permits"><Permits /></ModuleGuard>} />
            <Route path="/payouts" element={<ModuleGuard module="payouts"><Payouts /></ModuleGuard>} />
            <Route path="/accounting" element={<ModuleGuard module="accounting"><Accounting /></ModuleGuard>} />
            <Route path="/stock" element={<ModuleGuard module="stock"><Stock /></ModuleGuard>} />
            <Route path="/staff" element={<AdminOnly><Staff /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/hub" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
